import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCardMaintenancePageContext } from 'selectors/cardMaintenanceSelectors';
import { DataTable, Icon, useModal, ButtonType, ButtonColor } from '@ampol/design-system';
import { getPlasticTypesList, getPlasticType, createPlasticType, updatePlasticType } from 'actions/maintenanceActions';
import 'features/maintenance/pages/card-maintenance/card-maintenance.scss';

import PageHeader from 'components/layout/page-header/page-header';
import ContentControls from 'components/content-controls/content-controls';
import PlasticTypeForm from 'forms/plastic-type-form/plastic-type-form';
import { trackCustomEvent } from 'utilities/app-insights';

type PlasticTypeFormRequest = {
  [key: string]: unknown;
};

const CardMaintenance: React.FC = () => {
  const dispatch = useDispatch();

  const {
    plasticTypesListLoading,
    plasticTypesList,
    plasticTypesListResultSize,
    plasticType,
    createPlasticTypeLoading,
    createPlasticTypeLoaded,
    updatePlasticTypeLoading,
    updatePlasticTypeLoaded,
    loggedInUser,
    assignedAccountNumber,
  } = useSelector(selectCardMaintenancePageContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isEditPlasticType, setIsEditPlasticType] = useState(false);
  const [displayPlasticTypeForm, setDisplayPlasticTypeForm] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const offset = (pageNumber - 1) * pageSize;

  useEffect(() => {
    dispatch(getPlasticTypesList(pageSize, offset, sortColumn, sortDirection, searchTerm));
  }, [pageNumber, sortColumn, sortDirection, searchTerm]);

  useEffect(() => {
    if (createPlasticTypeLoaded || updatePlasticTypeLoaded) {
      dispatch(getPlasticTypesList(pageSize, offset, sortColumn, sortDirection, searchTerm));
    }
  }, [createPlasticTypeLoaded, updatePlasticTypeLoaded]);

  // Synchronizes React state with the design-system modal imperative API.
  // useModal returns stable open/close functions only after the modal has registered;
  // calling open/close directly in event handlers risks using a stale closure from the
  // first render (before ModalManager has mounted). The Effect always runs with the
  // current useModal result, making it the safe pattern for this external imperative API.
  useEffect(() => {
    if (displayPlasticTypeForm) {
      plasticTypeFormModal.open();
    } else {
      plasticTypeFormModal.close();
    }
  }, [displayPlasticTypeForm]);

  const onAddPlasticType = () => {
    setIsEditPlasticType(false);
    setDisplayPlasticTypeForm(true);
    trackCustomEvent(
      'Open Add Plastic Type Modal',
      loggedInUser,
      assignedAccountNumber,
      'Opened Add Plastic Type modal.'
    );
  };

  const onEditPlasticTypeClick = (id: string | number | undefined) => {
    if (!id) return;
    setIsEditPlasticType(true);
    dispatch(getPlasticType(id));
    setDisplayPlasticTypeForm(true);
    trackCustomEvent(
      'Open Edit Plastic Type Modal',
      loggedInUser,
      assignedAccountNumber,
      'Opened Edit Plastic Type modal by clicking a row on card maintenace list.',
      { selectedPlasticType: id }
    );
  };

  const onSearchTermChange = (input: string) => {
    trackCustomEvent('Search for Plastic Type', loggedInUser, assignedAccountNumber, 'Searched for Plastic Type.', {
      searchTerm: input,
    });
    setSearchTerm(input);
    setPageNumber(1);
  };

  const onTableColumnSorted = (column: string, isAscending: boolean) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  };

  const onPageNumberChange = (value: number) => {
    setPageNumber(value);
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of card maintenance data.'
    );
  };

  const handleSubmitPlasticType = (request: PlasticTypeFormRequest, id?: string | number) => {
    if (id) {
      dispatch(updatePlasticType(id, request));
      trackCustomEvent(
        'Submit Update Plastic Type',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Save to submit Update Plastic Type.',
        { selectedPlasticType: id }
      );
    } else {
      dispatch(createPlasticType(request));
      trackCustomEvent(
        'Submit Add Plastic Type',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Save to submit Add Plastic Type.'
      );
    }
    setDisplayPlasticTypeForm(false);
  };

  const plasticTypeFormModal = useModal({
    id: 'plastic-type-form-modal',
    title: 'Card Maintenance',
    content: (
      <PlasticTypeForm
        formId={'plastic-type-form'}
        isEdit={isEditPlasticType}
        plasticType={plasticType}
        onSubmit={handleSubmitPlasticType}
        onStateChange={() => {}}
      />
    ),
    formId: 'plastic-type-form',
    actions: [
      {
        label: 'Save',
        type:
          createPlasticTypeLoaded || createPlasticTypeLoading || updatePlasticTypeLoading
            ? ButtonType.Button
            : ButtonType.Submit,
        inactive: createPlasticTypeLoaded || createPlasticTypeLoading || updatePlasticTypeLoading,
      },
      {
        label: createPlasticTypeLoaded ? 'Close' : 'Cancel',
        onClick: () => setDisplayPlasticTypeForm(false),
      },
    ],
    color: ButtonColor.Blue,
    variant: 'triple',
    onClose: () => {
      trackCustomEvent(
        'Close Add/Edit Plastic Type Modal',
        loggedInUser,
        assignedAccountNumber,
        'Add or Edit Plastic Type Modal closed.'
      );
      setDisplayPlasticTypeForm(false);
    },
  });

  return (
    <>
      <PageHeader showButton={true} buttonLabel='Add plastic type' onButtonClick={onAddPlasticType} />
      <ContentControls
        showPageControls={true}
        showSearch={true}
        handleSearch={onSearchTermChange}
        pageNumber={pageNumber}
        onPageChange={onPageNumberChange}
        itemCount={plasticTypesListResultSize}
      />

      <div className={'card-maintenance__table-container'}>
        <DataTable
          caption={'Plastic types'}
          columns={[
            {
              header: 'Plastic Type Id',
              accessorKey: 'id',
              defaultSorted: 'asc',
            },
            {
              header: 'Short Name',
              accessorKey: 'shortName',
            },
            {
              header: 'Description',
              accessorKey: 'description',
            },
            {
              header: 'State',
              accessorKey: 'state',
            },
            {
              id: 'action',
              accessorFn: (x) => (
                <>
                  <button className={'card-maintenance__edit'} onClick={() => onEditPlasticTypeClick(x.id)}>
                    <Icon url={'/icons/edit.svg'} />
                  </button>
                </>
              ),
              rtl: true,
            },
          ]}
          onColumnSorted={onTableColumnSorted}
          isLoading={plasticTypesListLoading}
          columnTemplate={'1fr 1fr 3fr 1fr 70px'}
          data={plasticTypesList}
          tableVariant='list'
          onRowClick={(row) => onEditPlasticTypeClick(row.id)}
        />
      </div>
    </>
  );
};

export default CardMaintenance;
